<template>
	<Form @submit="handleSubmit" @invalid-submit="onInvalidSubmit">
		<!-- Loading overlay while orderdata/placeorder is in flight -->
		<Transition name="fade">
			<div
				v-if="checkoutStore.isPlacingOrder"
				class="fixed inset-0 z-[100] bg-surface-light/80 flex items-center justify-center"
			>
				<div class="flex flex-col items-center gap-4 text-text">
					<div class="w-10 h-10 rounded-full border-4 border-surface-grey border-t-primary animate-spin" />
					<span class="font-semibold">{{ $t('doorsale.placing_order') }}</span>
				</div>
			</div>
		</Transition>
		<DoorsaleLayout
			:title="$t('checkout.information')"
			:current-step="2"
		>
			<template #header-actions>
				<BaseButton
					v-if="giftCardAvailable"
					class="hidden md:mr-auto md:inline-flex"
					size="sm"
					color="default"
					@click="giftCardInput?.toggleGiftcardInput()"
				>
					{{ $t('checkout.redeem_gift_card') }}
				</BaseButton>
			</template>
			<template #aside>
				<CheckoutCodeInput
					v-if="giftCardAvailable"
					ref="giftCardInput"
					:has-gift-card="true"
				/>
				<div class="md:sticky md:top-24" v-if="cartStore.items.length > 0">
					<CheckoutSummary :primary-label="$t('doorsale.confirm_payment')" />
				</div>
			</template>

			<div v-if="cartStore.items.length > 0" class="flex flex-col gap-4">
				<div v-if="checkoutStore.startPurchaseData.require_email">
							<InputText
								name="customer-email"
								id="customer-email"
								type="email"
								:required="checkoutStore.startPurchaseData.require_email"
								:placeholder="$t('checkout.email_placeholder')"
								:rules="rules.email"
								v-model="cartStore.currentOrderData.email"
							/>
							<div class="text-sm mt-2">
							{{ $t('doorsale.order_confirmation_will_be_sent_to') }}
							</div>
						</div>

						<InputText
							v-if="checkoutStore.startPurchaseData.require_email"
							name="customer-email2"
							id="customer-email2"
							type="email"
							:required="checkoutStore.startPurchaseData.require_email"
							:placeholder="$t('checkout.repeat_email_placeholder')"
							:rules="rules.emailRepeat"
							v-model="cartStore.currentOrderData.email2"
						/>

						<InputText
							v-if="checkoutStore.startPurchaseData.require_name"
							name="customer-name"
							id="customer-name"
							type="text"
							required
							:placeholder="$t('checkout.name_placeholder')"
							:rules="rules.name"
							v-model="cartStore.currentOrderData.name"
						/>

						<InputText
							v-if="checkoutStore.startPurchaseData.require_address"
							name="customer-address"
							id="customer-address"
							type="text"
							:required="checkoutStore.startPurchaseData.require_address"
							:placeholder="$t('checkout.address_placeholder')"
							:rules="rules.address"
							v-model="cartStore.currentOrderData.address"
						/>

						<div class="flex gap-4" v-if="checkoutStore.startPurchaseData.require_city_zip">
							<InputText
								id="customer-zip"
								class="w-32"
								name="customer-zip"
								type="text"
								:required="checkoutStore.startPurchaseData.require_city_zip"
								:placeholder="$t('checkout.zip_placeholder')"
								:rules="rules.zip"
								v-model="cartStore.currentOrderData.zip"
							/>
							<InputText
								id="customer-city"
								class="flex-1"
								name="customer-city"
								type="text"
								:required="checkoutStore.startPurchaseData.require_city_zip"
								:placeholder="$t('checkout.city_placeholder')"
								:rules="rules.city"
								v-model="cartStore.currentOrderData.city"
							/>
						</div>

						<InputText
							v-if="checkoutStore.startPurchaseData.require_phone"
							name="customer-telephone"
							id="customer-telephone"
							type="tel"
							:required="checkoutStore.startPurchaseData.require_phone"
							:placeholder="$t('checkout.phone_placeholder')"
							:rules="rules.phone"
							v-model="cartStore.currentOrderData.telephone"
						/>

						<InputSelect
							v-if="checkoutStore.startPurchaseData.require_country"
							id="customer-country"
							name="customer-country"
							:required="checkoutStore.startPurchaseData.require_country"
							:options="countryOptions"
							v-model="cartStore.currentOrderData.country"
							:placeholder="$t('checkout.country_placeholder')"
							:rules="rules.country"
							:hide-title="false"
						/>

						<div v-if="cartStore.currentOrderData.customfields_data && cartStore.currentOrderData.customfields_data['per_event']">
							<CheckoutCustomFields
								type="ticket"
								:event-id="undefined"
								:ticket-id="undefined"
								:filters="['per_event']"
								:errors="undefined"
							/>
						</div>

						<div>
							<div v-if="checkoutStore.customFieldErrors.length > 0">
								<div v-for="(eventItem, eventIndex) in cartStore.items" :key="eventIndex">
									<div v-for="(ticketItem, ticketIndex) in cartStore.items[eventIndex].tickets" :key="ticketIndex">
										<div v-if="cartStore.currentOrderData.customfields_data &&
												cartStore.items[eventIndex].id &&
												cartStore.currentOrderData.customfields_data[cartStore.items[eventIndex].id] &&
												checkoutStore.customFieldErrors.length > 0">
											<BaseSlideTransition>
												<CheckoutCustomFields
													:type="cartStore.items[eventIndex].tickets[ticketIndex].upsale_flag ? 'upsaleTicket' : 'ticket'"
													:ticket-id="cartStore.items[eventIndex].tickets[ticketIndex].id"
													:event-id="cartStore.items[eventIndex].id"
													:filters="['per_ticket','per_tickettype']"
													:errors="checkoutStore.customFieldErrors"
												/>
											</BaseSlideTransition>
										</div>
									</div>
								</div>
							</div>
							<div class="mt-4 text-red-600" v-if="checkoutStore.orderErrors && checkoutStore.orderErrors.length > 0">
								<ul>
									<li v-for="error in checkoutStore.orderErrors" :key="error">
										{{ error }}
									</li>
								</ul>
							</div>
						</div>


			</div>
		</DoorsaleLayout>
	</Form>
</template>

<script setup lang="ts">
	import { useHead } from '@unhead/vue';
	import { Form, type InvalidSubmissionHandler, type SubmissionHandler } from 'vee-validate';
	import { computed, ref, watch } from 'vue';
	import { useI18n } from 'vue-i18n';
	import { useRouter } from 'vue-router';
	import * as yup from 'yup';

	import CheckoutCustomFields from '@/components/checkout/CheckoutCustomFields.vue';
	import CheckoutCodeInput from '@/components/checkout/CheckoutCodeInput.vue';
	import CheckoutSummary from '@/components/checkout/CheckoutSummary.vue';
	import DoorsaleLayout from '@/components/doorsale/DoorsaleLayout.vue';
	import BaseButton from '@/components/ui/BaseButton.vue';
	import BaseSlideTransition from '@/components/ui/BaseSlideTransition.vue';
	import InputCheckbox from '@/components/ui/inputs/InputCheckbox.vue';
	import InputSelect from '@/components/ui/inputs/InputSelect.vue';
	import InputText from '@/components/ui/inputs/InputText.vue';
	import { useCartStore } from '@/stores/cart';
	import { useCheckoutStore } from '@/stores/checkoutStore';
	import { useGeneralStore } from '@/stores/general';
	import { useSeatmapStore } from '@/stores/seatmapStore';
	import { getCityFromZip } from '@/utils/getCityFromZip';
	import { setNonRequiredFields } from '@/utils/setNonRequiredFields';
	import { useWebsocketStore } from '@/websocket/websocket';

	const { t, locale } = useI18n();
	const router = useRouter();

	const cartStore = useCartStore();
	const checkoutStore = useCheckoutStore();
	const generalStore = useGeneralStore();
	const seatmapStore = useSeatmapStore();
	const websocketStore = useWebsocketStore();
	const giftCardInput = ref<InstanceType<typeof CheckoutCodeInput> | null>(null);
	const giftCardAvailable = computed(() =>
		cartStore.items.some(item => item.giftcard_allowed) && cartStore.giftcardAllowed()
	);

	websocketStore.send('startpurchase', { language: locale.value }, undefined);
	checkoutStore.orderErrors = [];

	// Pre-fill vouchercode data into order fields once, on first non-empty value
	watch(() => checkoutStore.startPurchaseData.vouchercode_data, (newVal) => {
		if (!checkoutStore.vouchercodeDataSet && newVal && Object.keys(newVal).length > 0) {
			for (const [key, value] of Object.entries(newVal)) {
				if (key in cartStore.currentOrderData) {
					(cartStore.currentOrderData as Record<string, unknown>)[key] = value;
				}
				if (key === 'email') {
					cartStore.currentOrderData.email2 = value;
				}
			}
			checkoutStore.vouchercodeDataSet = true;
		}
	}, { immediate: true });

	// Auto-fill Danish city from zip
	watch(() => cartStore.currentOrderData.zip, (newZip) => {
		if (newZip && (newZip.length == 3 || newZip.length == 4)) {
			const city = getCityFromZip(Number(newZip));
			if (city) {
				cartStore.currentOrderData.city = city;
			}
		}
	});

	setNonRequiredFields();

	// Redirect to event list if cart is empty (allow time for cart to hydrate from localStorage)
	setTimeout(() => {
		if (cartStore.items.length == 0) {
			router.push({ name: 'doorsale-events' });
		}
	}, 2000);

	const countryOptions = computed(() => {
		return checkoutStore.startPurchaseData.countries.map((country: string) => ({
			value: country,
			label: country,
		}));
	});

	watch(countryOptions, (newOptions) => {
		if (newOptions.length > 0 && (!cartStore.currentOrderData.country || cartStore.currentOrderData.country == '')) {
			cartStore.currentOrderData.country = newOptions[0].value;
		}
	}, { immediate: true });

	const rules = {
		email: yup
			.string()
			.email(t('validation.email'))
			.required(t('validation.required')),
		emailRepeat: yup
			.string()
			.email(t('validation.email'))
			.required(t('validation.required'))
			.test(
				'emails-match',
				t('validation.emails_must_match'),
				value => value === cartStore.currentOrderData.email
			),
		name: yup.string().required(t('validation.required')),
		address: yup.string().required(t('validation.required')),
		zip: yup
			.string()
			.matches(/^\d{1,25}$/, t('validation.zip'))
			.required(t('validation.required')),
		city: yup.string().required(t('validation.required')),
		phone: yup
			.string()
			.matches(/^[\d +()-]{7,}$/, t('validation.phone'))
			.required(t('validation.required')),
		country: yup.string().required(t('validation.required')),
	};

	const onInvalidSubmit: InvalidSubmissionHandler = submission => {
		const firstErrorFieldName = Object.keys(submission.errors)[0];
		const el = document.querySelector(`[name="${firstErrorFieldName}"]`);
		if (el) {
			el.scrollIntoView();
		}
	};

	const handleSubmit: SubmissionHandler = () => {
		setNonRequiredFields();
		cartStore.currentOrderData.price_group_info = seatmapStore.buildAllPriceGroupInfo();
		checkoutStore.setOrderData(cartStore.currentOrderData);
		checkoutStore.isPlacingOrder = true;
		checkoutStore.orderErrors = [];
		websocketStore.send('orderdata', cartStore.currentOrderData, undefined);
	};

	useHead({
		title: t('checkout.information'),
		meta: [{ property: 'og:title', content: t('checkout.information') }],
	});
</script>
